import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { CertificateDownload } from '../types/models/Certificate';
import type { ApiResponseCertificateDownload } from '../types/api/ApiResponseCertificate';

interface UseCertificatePemReturn {
  download: CertificateDownload | null;
  loading: boolean;
  error: string | null;
  // HTTP status accompanying `error`, so the caller can branch on the
  // documented non-fault cases (404: no PEM cached locally yet OR no
  // certificate row anymore; 409: not issued yet). Only set when the
  // response body parsed as JSend — a reverse-proxy error page's status
  // must not be dressed up as a documented lifecycle state — and null for
  // network-level failures without a response.
  errorStatus: number | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch a certificate's leaf PEM via the Download endpoint's default mode
 * (GET /datacenters/certificates/{id}/download — locally cached PEM, no
 * upstream call, `private_key` is always null here).
 *
 * Used by the detail page's PEM tab. The consuming tab must be mounted
 * lazily (`mountOnEnter` on the parent Tabs) and pair with a Refresh action,
 * per the detail-page tab convention.
 */
export const useCertificatePem = (certificateId: number): UseCertificatePemReturn => {
  const [download, setDownload] = useState<CertificateDownload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchPem = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorStatus(null);

      const response = await apiFetch(`/datacenters/certificates/${certificateId}/download`, {
        cache: 'no-store',
      });
      let body: ApiResponseCertificateDownload | null;
      try {
        body = (await response.json()) as ApiResponseCertificateDownload;
      } catch {
        body = null;
      }

      if (body?.status === 'success' && body.data?.download) {
        setDownload(body.data.download);
        return;
      }

      setError(
        body?.message ??
          `Failed to load the certificate PEM (HTTP ${`${response.status} ${response.statusText}`.trim()})`,
      );
      if (body !== null) {
        setErrorStatus(response.status);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the certificate PEM');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, certificateId]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchPem();
    }
  }, [auth.isAuthenticated, fetchPem]);

  return { download, loading, error, errorStatus, refetch: fetchPem };
};
